/* eslint-disable no-unused-vars */
import { ReactNode } from 'react';
import styled, { css } from 'styled-components';

export const StyledList = styled.ul`
  display: flex;
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const StyledItem = styled.li`
  display: block;
  flex-shrink: 0;

  &:first-child {
     margin-right: 12px;
  }

  &:last-child {
     margin-left: 12px;
  }
`;

export interface StyledButtonProps {
  children?: ReactNode;
  as?: string;
  href?: string;
  startOfBreak?: boolean;
  endOfBreak?: boolean;
  isPageButton?: boolean;
  active?: boolean;
}

export const StyledButton = styled.button<StyledButtonProps>`
  display: flex;
  position: relative;
  justify-content: center;
  align-items: center;
  min-width: 40px;
  padding: 0 8px;
  height: 40px;
  cursor: pointer;
  outline: none;
  user-select: none;
  border: 0;
  color: ${({ theme }) => theme.colors.gray13};
  text-decoration: none;
  background: ${({ theme }) => theme.colors.white};
  padding: 0;
  box-sizing: border-box;

  &:after {
    content: '';
    border: 1px solid ${({ theme }) => theme.colors.gray13};
    display: block;
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    right: 0;
    pointer-events: none;
    z-index: 10;
  }

  ${({ startOfBreak }) => startOfBreak && css`
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;

    &:after {
      border-top-left-radius: 4px;
      border-bottom-left-radius: 4px;
    }
  `}

  ${({ endOfBreak }) => endOfBreak && css`
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;

    &:after {
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
    }
  `}

  ${({ startOfBreak, isPageButton }) => isPageButton && !startOfBreak && css`
    &:after {
      left: -1px;
    }
  `}

  ${({ isPageButton, endOfBreak }) => isPageButton && !endOfBreak && css`
    &:hover:not(.ia-react-core_pagination--active) {
      &:after {
        right: -1px;
      }
    }
  `}

  &:disabled {
    color: ${({ theme }) => theme.colors.disabled};
    cursor: default;

    &:after {
      border-color: ${({ theme }) => theme.colors.disabled};
    }
  }

  &:not(:disabled) {
    &:hover {
      color: ${({ theme }) => theme.colors.light};
      font-weight: bold;
      z-index: 1;

      &:after {
        border-color: ${({ theme }) => theme.colors.light};
        border-width: 2px;
      }
    }

    &:focus-visible:not(:hover) {
      z-index: 4;
    }

    &:focus-visible:not(:hover), &:focus:not(:hover) {
      box-shadow: 0 0 3px 0 ${({ theme }) => theme.colors.lighter};

      &:after {
        border-width: 2px;
      }
    }

    &:focus:not(&:focus-visible):not(:hover) {
      box-shadow: none;
    }

    &:focus:not(&:focus-visible):not(.ia-react-core_pagination--active):not(:hover) {
      &:after {
        border-width: 1px;
      }
    }

    &:active:not(:disabled) {
      color: ${({ theme }) => theme.colors.lighter};

      &:after {
        border-color: ${({ theme }) => theme.colors.lighter};
        border-width: 2px;
      }
    }
  }

  ${({ active }) => active && css`
    background: ${({ theme }) => theme.colors.blue12};
    font-weight: bold;
    color: ${({ theme }) => theme.colors.light}; 
    z-index: 3;

    &:after {
      border-width: 2px;
      border-color: ${({ theme }) => theme.colors.light};
      right: -1px;
    }
  `}

  &:not(:nth-last-child(2)):not(:first-child) {
    &:after {
      right: -1px;

      &:hover {
        right: -2px;
      }
    }
  }
`;

export const StyledBreak = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
`;

export const StyledCompactBreak = styled(StyledBreak)`
  width: auto;
  white-space: nowrap;
  font-size: 14px;
`;

export const StyledNavButton = styled(StyledButton)`
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.primary};
  margin-right: 0;
  
  &:after {
    border-color: ${({ theme }) => theme.colors.primary};
    border-radius: 4px;
  }

  &:not(:nth-child(2)), :not(:nth-last-child(2)), :not(:nth-child(2)):not(:nth-last-child(2)) {
    width: 40px;
  }
`;
